import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { Button } from '../components/ui/button';
import { Calendar, Wine, UtensilsCrossed, Users, Trash2, ChevronDown, Search, UserCheck } from 'lucide-react';
import { Input } from '../components/ui/input';
import { format, parseISO } from 'date-fns';
import { toast } from 'sonner';

export default function MealHistoryPage() {
  const { authHeaders, API } = useAuth();
  const [meals, setMeals] = useState([]);
  const [restaurants, setRestaurants] = useState([]);
  const [filterRestaurant, setFilterRestaurant] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(true);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [expandedMeal, setExpandedMeal] = useState(null);

  const fetchMeals = async () => {
    try {
      const params = {};
      if (filterRestaurant && filterRestaurant !== 'all') params.restaurant_id = filterRestaurant;
      const [mealsRes, restRes] = await Promise.all([
        axios.get(`${API}/meals`, { headers: authHeaders(), params }),
        axios.get(`${API}/restaurants`, { headers: authHeaders() })
      ]);
      setMeals(mealsRes.data);
      setRestaurants(restRes.data);
    } catch (err) {
      console.error('Failed to fetch meals', err);
    }
    setLoading(false);
  };

  useEffect(() => { fetchMeals(); }, [filterRestaurant]);

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      await axios.delete(`${API}/meals/${deleteTarget}`, { headers: authHeaders() });
      setMeals(meals.filter(m => m.id !== deleteTarget));
      toast.success('Meal deleted');
    } catch (err) {
      toast.error('Failed to delete');
    }
    setDeleteTarget(null);
  };

  const formatDate = (dateStr) => {
    try { return format(parseISO(dateStr), "EEE, MMM do"); }
    catch { return dateStr; }
  };

  const formatTime = (dateStr) => {
    try { return format(parseISO(dateStr), "h:mm a"); }
    catch { return ''; }
  };

  const filteredMeals = meals.filter(meal => {
    if (!searchQuery) return true;
    const q = searchQuery.toLowerCase();
    return meal.restaurant_name.toLowerCase().includes(q) ||
      meal.companions.some(c => c.toLowerCase().includes(q)) ||
      meal.items.some(it => it.name.toLowerCase().includes(q)) ||
      (meal.notes && meal.notes.toLowerCase().includes(q));
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="animate-pulse text-lg" style={{ color: '#5E646E' }}>Loading...</div>
      </div>
    );
  }

  return (
    <div data-testid="meal-history-page" className="pb-nav px-4 md:px-6 max-w-2xl mx-auto fade-in">
      <div className="pt-6 pb-4">
        <h1 className="text-3xl font-semibold tracking-tight" style={{ fontFamily: 'Outfit, sans-serif', color: '#1A1D21' }}>
          Meal History
        </h1>
        <p className="text-sm mt-1" style={{ color: '#5E646E' }}>{meals.length} meals logged</p>
      </div>

      {/* Filters */}
      <div className="flex gap-2 mb-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4" style={{ color: '#5E646E' }} />
          <Input
            data-testid="search-meals"
            className="h-12 pl-10 text-base rounded-xl"
            style={{ background: '#FFFFFF', borderColor: '#E8E6E0' }}
            placeholder="Search..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>
        <Select value={filterRestaurant} onValueChange={setFilterRestaurant}>
          <SelectTrigger data-testid="filter-restaurant" className="h-12 w-40 rounded-xl" style={{ background: '#FFFFFF', borderColor: '#E8E6E0' }}>
            <SelectValue placeholder="All places" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All places</SelectItem>
            {restaurants.map(r => (
              <SelectItem key={r.id} value={r.id}>{r.name}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Meals List */}
      {filteredMeals.length === 0 ? (
        <div className="bg-white rounded-2xl border p-8 text-center" style={{ borderColor: '#E8E6E0' }}>
          <UtensilsCrossed className="w-10 h-10 mx-auto mb-3" style={{ color: '#C76D5B' }} />
          <p className="text-base font-medium" style={{ color: '#1A1D21' }}>No meals found</p>
          <p className="text-sm mt-1" style={{ color: '#5E646E' }}>
            {searchQuery ? 'Try a different search' : 'Start logging your meals'}
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          {filteredMeals.map((meal, i) => {
            const isExpanded = expandedMeal === meal.id;
            const drinkCount = meal.items.filter(it => it.item_type === 'drink').reduce((sum, it) => sum + it.quantity, 0);
            const mealCount = meal.items.filter(it => it.item_type === 'meal').reduce((sum, it) => sum + it.quantity, 0);

            return (
              <div
                key={meal.id}
                data-testid={`meal-card-${i}`}
                className="bg-white rounded-2xl border overflow-hidden transition-all"
                style={{ borderColor: '#E8E6E0' }}
              >
                <button
                  data-testid={`meal-expand-${i}`}
                  onClick={() => setExpandedMeal(isExpanded ? null : meal.id)}
                  className="w-full p-4 text-left"
                >
                  <div className="flex items-start justify-between">
                    <div>
                      <h3 className="text-base font-medium" style={{ color: '#1A1D21' }}>
                        {meal.restaurant_name}
                      </h3>
                      <div className="flex items-center gap-3 mt-1">
                        <span className="flex items-center gap-1 text-sm" style={{ color: '#5E646E' }}>
                          <Calendar className="w-3.5 h-3.5" /> {formatDate(meal.meal_date)}
                        </span>
                        <span className="text-sm" style={{ color: '#5E646E' }}>{formatTime(meal.meal_date)}</span>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="flex gap-1">
                        {(meal.covers || 1) > 0 && (
                          <span className="text-xs px-2 py-1 rounded-full" style={{ background: 'rgba(46,92,85,0.1)', color: '#2E5C55' }}>
                            {meal.covers || 1} cover{(meal.covers || 1) > 1 ? 's' : ''}
                          </span>
                        )}
                        {mealCount > 0 && (
                          <span className="text-xs px-2 py-1 rounded-full" style={{ background: 'rgba(0,90,141,0.08)', color: '#005A8D' }}>
                            {mealCount} meal{mealCount > 1 ? 's' : ''}
                          </span>
                        )}
                        {drinkCount > 0 && (
                          <span className="text-xs px-2 py-1 rounded-full" style={{ background: 'rgba(199,109,91,0.12)', color: '#C76D5B' }}>
                            {drinkCount} drink{drinkCount > 1 ? 's' : ''}
                          </span>
                        )}
                      </div>
                      <ChevronDown
                        className="w-5 h-5 transition-transform"
                        style={{ color: '#C8C6BF', transform: isExpanded ? 'rotate(180deg)' : 'none' }}
                      />
                    </div>
                  </div>
                </button>

                {isExpanded && (
                  <div className="px-4 pb-4 border-t fade-in" style={{ borderColor: '#F0EFEA' }}>
                    {/* Items */}
                    <div className="mt-3">
                      <p className="text-xs uppercase tracking-wider mb-2" style={{ color: '#5E646E' }}>Items</p>
                      <div className="space-y-1.5">
                        {meal.items.map((item, j) => (
                          <div key={j} className="flex items-center gap-2 text-sm" style={{ color: '#1A1D21' }}>
                            {item.item_type === 'drink' ?
                              <Wine className="w-4 h-4" style={{ color: '#C76D5B' }} /> :
                              <UtensilsCrossed className="w-4 h-4" style={{ color: '#005A8D' }} />
                            }
                            <span>{item.quantity > 1 ? `${item.quantity}x ` : ''}{item.name}</span>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Covers */}
                    {(meal.covers || 1) > 0 && (
                      <div className="mt-3">
                        <p className="text-xs uppercase tracking-wider mb-1" style={{ color: '#5E646E' }}>Covers</p>
                        <div className="flex items-center gap-2 text-sm" style={{ color: '#1A1D21' }}>
                          <UserCheck className="w-4 h-4" style={{ color: '#2E5C55' }} />
                          <span>{meal.covers || 1} {(meal.covers || 1) === 1 ? 'person' : 'people'} on your tab</span>
                        </div>
                      </div>
                    )}

                    {/* Companions */}
                    {meal.companions.length > 0 && (
                      <div className="mt-3">
                        <p className="text-xs uppercase tracking-wider mb-2" style={{ color: '#5E646E' }}>With</p>
                        <div className="flex flex-wrap gap-1.5">
                          {meal.companions.map(c => (
                            <Badge
                              key={c}
                              className="text-sm rounded-full px-3 py-1 font-normal"
                              style={{ background: 'rgba(199,109,91,0.12)', color: '#C76D5B', border: 'none' }}
                            >
                              {c}
                            </Badge>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Notes */}
                    {meal.notes && (
                      <div className="mt-3">
                        <p className="text-xs uppercase tracking-wider mb-1" style={{ color: '#5E646E' }}>Notes</p>
                        <p className="text-sm" style={{ color: '#1A1D21' }}>{meal.notes}</p>
                      </div>
                    )}

                    {/* Delete */}
                    <button
                      data-testid={`delete-meal-${i}`}
                      onClick={() => setDeleteTarget(meal.id)}
                      className="mt-3 flex items-center gap-1.5 text-sm font-medium touch-active"
                      style={{ color: '#B93838' }}
                    >
                      <Trash2 className="w-4 h-4" /> Delete this entry
                    </button>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Delete Confirmation */}
      <Dialog open={!!deleteTarget} onOpenChange={() => setDeleteTarget(null)}>
        <DialogContent className="rounded-2xl" style={{ background: '#FFFFFF' }}>
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Outfit, sans-serif' }}>Delete Meal?</DialogTitle>
            <DialogDescription>This action cannot be undone. The meal log will be permanently removed.</DialogDescription>
          </DialogHeader>
          <div className="flex gap-3 mt-2">
            <Button
              data-testid="cancel-delete"
              variant="outline"
              onClick={() => setDeleteTarget(null)}
              className="flex-1 h-12 rounded-full"
            >
              Cancel
            </Button>
            <Button
              data-testid="confirm-delete"
              onClick={handleDelete}
              className="flex-1 h-12 rounded-full"
              style={{ background: '#B93838', color: '#FFFFFF' }}
            >
              Delete
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
